import React, { useEffect, useState } from "react";
import { HandshakeIcon } from "../components/HandshakeIcon";
import { handshakeSpendRain, handshakeRain } from "../lib/hotdogRain";
import { PremiumCard, BundleBanner, PurchaseCelebration } from "../components/shop/PremiumUI";
import { useLocation, useNavigate } from "react-router-dom";
import api, { ASSETS } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ShoppingCart, Sparkles, Crown, Gift, Zap, Loader2, Handshake, Trophy, Apple, Flame, BatteryCharging, Users, Plane, ChevronDown, Star } from "lucide-react";
import { bonusText as fmtBonus } from "../components/vault/VaultUI";
import { SLOT_META, SlotChip, InfoPill, CategoryHeader, ShopFrame, SecretHandshake } from "../components/shop/ShopUI";

const BULK_IDLE_LINES = [
  "BROTHER!",
  "You need to bulk up, brother.",
  "Hot dogs and handshakes are how we do business around here!",
  "LOOK AT THESE 24-INCH PYTHONS! DON'T GET JEALOUS, KID!",
];

const BULK_CHEER_LINES = [
  "BROTHER! NOW THAT'S HOW YOU MAKE GAINS!",
  "SAY YOUR PRAYERS, EAT YOUR VITAMINS!",
];

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function BulkHoganBanner() {
  const [text, setText] = useState(BULK_IDLE_LINES[0]);
  const [visible, setVisible] = useState(true);
  const [tick, setTick] = useState(0); // forces re-mount for pop animation
  const [socked, setSocked] = useState(false);
  const cheerUntilRef = React.useRef(0);

  // Cheer trigger — fires on any purchase via CustomEvent
  useEffect(() => {
    const onCheer = (e) => {
      const line = e.detail?.line || pickRandom(BULK_CHEER_LINES);
      cheerUntilRef.current = Date.now() + (e.detail?.durationMs || 3200);
      setText(line);
      setVisible(true);
      setTick((t) => t + 1);
    };
    window.addEventListener("bulk-cheer", onCheer);
    return () => window.removeEventListener("bulk-cheer", onCheer);
  }, []);

  const findSock = async () => {
    if (socked) return; // one-shot per page load; backend still handles repeats
    setSocked(true);
    try {
      const r = await api.post("/shop/easter-egg/boots_mr_stinko");
      window.dispatchEvent(new CustomEvent("bulk-cheer", {
        detail: { line: "WHATCHA GONNA DO WHEN THE SOCK COMES FOR YOU?!", durationMs: 4200 },
      }));
      if (r.data?.first_time) {
        toast.success("🧦 EASTER EGG! Mr. Stinko added to your gear collection!");
      } else {
        toast.success(`Mr. Stinko level up! (×${r.data.copies})`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setSocked(false);
    }
  };

  // Idle loop — pauses while a cheer is on-screen
  useEffect(() => {
    let mounted = true;
    let i = 0;
    const loop = async () => {
      while (mounted) {
        // Skip while a cheer is active
        if (Date.now() < cheerUntilRef.current) {
          await new Promise((r) => setTimeout(r, 400));
          continue;
        }
        setText(BULK_IDLE_LINES[i % BULK_IDLE_LINES.length]);
        setVisible(true);
        setTick((t) => t + 1);
        i++;
        await new Promise((r) => setTimeout(r, 2200));
        if (i % BULK_IDLE_LINES.length === 0) {
          if (!mounted) return;
          setVisible(false);
          await new Promise((r) => setTimeout(r, 5000));
        }
      }
    };
    loop();
    return () => { mounted = false; };
  }, []);

  return (
    <div
      data-testid="shop-hero-banner"
      className="wr-rise relative rounded-2xl overflow-hidden border-2 border-[#FF007F]/70 mb-6"
      style={{ boxShadow: "0 0 36px #FF007F55, 0 0 80px #00F0FF22" }}
    >
      <img
        src="/shop_banner.webp"
        alt="Bulk Hogan's Beach Shop"
        className="w-full h-auto block"
        draggable={false}
      />

      {/* Hidden easter-egg hotspot: the sock in the shop at pixel (70,250) on a 1376x624 banner.
          Percentages keep it locked to the sock regardless of banner scaling. */}
      <button
        type="button"
        data-testid="shop-easter-sock"
        aria-label="Mysterious sock"
        onClick={findSock}
        className="absolute cursor-pointer group focus:outline-none"
        style={{
          left:   `${(70 / 1376) * 100}%`,
          top:    `${(250 / 624) * 100}%`,
          width:  `${(50 / 1376) * 100}%`,
          height: `${(50 / 624) * 100}%`,
          background: "transparent",
          border: "none",
          padding: 0,
        }}
        title=""
      >
        {/* Barely-visible outline on hover, invisible otherwise */}
        <span className="absolute inset-0 rounded-sm ring-0 group-hover:ring-2 group-hover:ring-[#FFE600]/70 transition-all" />
      </button>
      {/* Speech bubble: right edge anchored at horizontal center of banner, tail points right toward Bulk's face */}
      <div className="absolute left-1/2 top-[22%] sm:top-[24%] pointer-events-none" style={{ transform: "translate(-100%, -50%)" }}>
        {visible && (
          <div
            key={tick}
            data-testid="shop-bubble"
            className="relative bg-white text-[#0A051A] font-heading uppercase tracking-wide rounded-2xl px-2.5 py-1.5 sm:px-4 sm:py-2.5 border-2 border-[#0A051A] shadow-[0_4px_0_#0A051A] text-[9px] sm:text-sm md:text-base max-w-[40vw] sm:max-w-[380px] sm:whitespace-normal leading-tight"
            style={{ animation: "bulk-bubble-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)" }}
          >
            {text}
            <span
              className="absolute -bottom-2 right-4 w-0 h-0"
              style={{
                borderLeft: "10px solid transparent",
                borderRight: "10px solid transparent",
                borderTop: "12px solid #FFFFFF",
                filter: "drop-shadow(0 2px 0 #0A051A)",
              }}
            />
          </div>
        )}
      </div>
      <style>{`
        @keyframes bulk-bubble-pop {
          0%   { opacity: 0; transform: scale(0.4) rotate(-6deg); }
          70%  { opacity: 1; transform: scale(1.08) rotate(2deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
      `}</style>
    </div>
  );
}

function bonusText(bonus) {
  return Object.entries(bonus || {}).map(([k, v]) => fmtBonus(k, v)).join(" · ");
}

function DailyResetTimer({ iso, onReset }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = iso ? new Date(iso).getTime() - now : null;
  useEffect(() => {
    if (diff === null || diff > 0) return;
    const t = setTimeout(onReset, 15000);
    return () => clearTimeout(t);
  }, [diff !== null && diff <= 0]); // eslint-disable-line react-hooks/exhaustive-deps
  const pad = (n) => String(n).padStart(2, "0");
  let label = "--:--:--";
  if (diff !== null) {
    if (diff <= 0) label = "Resetting…";
    else {
      const s = Math.floor(diff / 1000);
      label = `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
    }
  }
  return (
    <div
      data-testid="shop-daily-reset-timer"
      title="Time until the daily reset — shop stock refreshes."
      className="ml-auto px-3.5 py-2 rounded-full font-heading uppercase tracking-widest text-[11px] border-2 bg-[#0A051A]/90 backdrop-blur"
      style={{ color: "#00F0FF", borderColor: "#00F0FF", boxShadow: "0 0 12px rgba(0,240,255,0.4)" }}
    >
      Daily Reset <span className="lb-num font-bold ml-1" data-testid="shop-daily-reset-countdown">{label}</span>
    </div>
  );
}

function HotDogShop() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [bonusPct, setBonusPct] = useState(0);
  const [nextReset, setNextReset] = useState(null);
  const [busy, setBusy] = useState(null);
  const [slot, setSlot] = useState("all");

  const loadShop = async () => {
    const r = await api.get("/shop/items");
    // Backend returns {items, restock_bonus_pct}; keep legacy array shape as a safety net.
    if (Array.isArray(r.data)) {
      setItems(r.data.map(it => ({ ...it, remaining: it.remaining ?? 0, max_today: it.max_today ?? 0 })));
      setBonusPct(0);
    } else {
      setItems(r.data.items || []);
      setBonusPct(r.data.restock_bonus_pct || 0);
      setNextReset(r.data.next_daily_reset_at || null);
    }
  };
  useEffect(() => { loadShop(); }, []);

  const buy = async (id) => {
    setBusy(id);
    try {
      await api.post("/shop/buy", { item_id: id });
      await Promise.all([refresh(), loadShop()]);
      toast.success("Item added to inventory!");
      window.dispatchEvent(new CustomEvent("bulk-cheer"));
      window.dispatchEvent(new Event("achievements-check"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const buyAll = async (id) => {
    setBusy(id + ":all");
    try {
      const r = await api.post("/shop/buy-all", { item_id: id });
      await Promise.all([refresh(), loadShop()]);
      toast.success(`Bought x${r.data.qty} for ${r.data.spent.toLocaleString()} Hot Dogs!`);
      window.dispatchEvent(new CustomEvent("bulk-cheer"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const SLOT_IDS = ["all", "face", "jacket", "boots", "food"];
  const count = (s) => items.filter(i => i.slot === s).length;
  const groups = (slot === "all" ? SLOT_IDS.slice(1) : [slot]).map((id) => ({ id, list: items.filter(i => i.slot === id) })).filter(g => g.list.length);
  const others = slot === "all" ? items.filter(i => !SLOT_META[i.slot]) : [];
  if (others.length) groups.push({ id: "all", list: others });

  const renderItem = (it, idx) => {
    const m = SLOT_META[it.slot] || SLOT_META.all;
    const remaining = it.remaining ?? 0;
    const max_today = it.max_today ?? 0;
    const soldOut = remaining <= 0;
    const canAfford = (user?.hot_dogs ?? 0) >= it.price;
    const stockPct = max_today > 0 ? Math.max(0, Math.min(100, (remaining / max_today) * 100)) : 0;
    const stockColor = soldOut ? "#FF007F" : (stockPct < 20 ? "#FFE600" : "#39FF14");
    const SlotIcon = m.icon;
    return (
      <ShopFrame key={it.id} testId={`shop-item-${it.id}`} color={m.color} alt={m.alt} dim={soldOut} delay={Math.min(idx, 12) * 40}>
        <div className="relative p-2.5 flex flex-col h-full">
          <div className="relative aspect-square rounded-xl bg-[#0A051A] mb-2 overflow-hidden border-2" style={{ borderColor: `${m.color}66` }}>
            <img src={it.image || `/items/${it.id}.webp`} alt={it.name}
                 className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                 onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <span className="wr-tile-shine pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 opacity-0 group-hover:opacity-100 group-hover:animate-[wrTileShine_0.8s_ease-out]" />
            <div className="absolute top-1.5 left-1.5 w-7 h-7 rounded-lg flex items-center justify-center bg-black/70 backdrop-blur border" style={{ borderColor: m.color }}>
              <SlotIcon size={14} strokeWidth={2.6} style={{ color: m.color, filter: `drop-shadow(0 0 4px ${m.color})` }} />
            </div>
            {soldOut && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                <span className="font-graffiti text-xl -rotate-12 px-2 border-2 rounded" style={{ color: "#FF007F", borderColor: "#FF007F", textShadow: "0 0 10px #FF007F" }}>
                  Sold Out
                </span>
              </div>
            )}
          </div>
          <div className="font-graffiti text-base leading-tight truncate text-white" style={{ textShadow: `0 0 8px ${m.color}66, 2px 2px 0 #000` }} title={it.name}>{it.name}</div>
          <div className="text-[10px] font-mono text-[#39FF14] mb-1.5 truncate" title={bonusText(it.bonus)}>{bonusText(it.bonus)}</div>
          <div data-testid={`stock-${it.id}`} className="flex items-center justify-between text-[10px] font-mono mb-1" style={{ color: stockColor }}
               title={`Daily stock refreshes at the next daily reset`}>
            <span className="uppercase tracking-widest opacity-80">Stock</span>
            <span className="lb-num font-bold">{remaining.toLocaleString()} / {max_today.toLocaleString()}</span>
          </div>
          <div className="h-1.5 w-full bg-[#1a0a2a] border border-[#39194D] rounded-full overflow-hidden mb-2">
            <div className="h-full transition-[width] duration-500" style={{ width: `${stockPct}%`, background: stockColor, boxShadow: `0 0 6px ${stockColor}88` }} />
          </div>
          <div className="mt-auto flex flex-col gap-1.5">
            <div className="flex items-center gap-1 min-w-0">
              <img src={ASSETS.currency.hotdog} alt="" className="w-5 h-5 shrink-0" />
              <span className="lb-num font-bold text-base truncate" style={{ color: canAfford ? "#FFE600" : "#FF6B9A", textShadow: "0 0 8px #FFE60066" }}>{it.price.toLocaleString()}</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <button data-testid={`buy-${it.id}`} disabled={busy === it.id || busy === it.id + ":all" || soldOut || !canAfford} onClick={() => buy(it.id)}
                      className="btn-neon !px-2 !py-1.5 text-[10px] flex items-center justify-center gap-1">
                <ShoppingCart size={12} /> {busy === it.id ? "..." : "BUY"}
              </button>
              <button data-testid={`buy-all-${it.id}`} disabled={busy === it.id || busy === it.id + ":all" || soldOut || !canAfford} onClick={() => buyAll(it.id)}
                      title={`Buy as many as you can afford (up to ${remaining} left today)`}
                      className="btn-neon !px-2 !py-1.5 text-[10px] whitespace-nowrap" style={{ background: "transparent", color: "#39FF14", borderColor: "#39FF14" }}>
                {busy === it.id + ":all" ? "..." : "BUY ALL"}
              </button>
            </div>
          </div>
        </div>
      </ShopFrame>
    );
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="hotdog-slot-filter">
        {SLOT_IDS.map(id => (
          <SlotChip key={id} id={id} active={slot === id} count={id === "all" ? items.length : count(id)} onClick={() => setSlot(id)} />
        ))}
        <DailyResetTimer iso={nextReset} onReset={loadShop} />
        <InfoPill testId="shop-restock-bonus" color="#39FF14" label="Restock Bonus"
                  title="Restock bonus — increases how many of each item you can buy per day. Resets at the daily reset.">+{bonusPct}%</InfoPill>
      </div>
      <div className="space-y-7">
        {groups.map((g) => (
          <section key={g.id} data-testid={`shop-group-${g.id}`}>
            {slot === "all" && g.id !== "all" && <CategoryHeader id={g.id} count={g.list.length} />}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {g.list.map(renderItem)}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

// Rising-cost "stack" items metadata (icon + accent color + where to navigate).
const STACK_META = {
  food_slots:       { icon: Apple,            color: "#FF9500", nav: "/roster" },
  card_drafts:      { icon: Crown,            color: "#9D00FF", nav: "/cards" },
  free_sparks:      { icon: Sparkles,         color: "#FFE600" },
  pyro_energy_cap:  { icon: BatteryCharging,  color: "#39FF14" },
  double_induction: { icon: Users,            color: "#00F0FF", nav: "/hof" },
  tour_planes:      { icon: Plane,            color: "#4D9FFF" },
  card_star:        { icon: Star,             color: "#FF3399", nav: "/cards" },
};

/* =========================================================
   Purchase burst — explodes a radial burst of neon particles
   over the card the player just bought. Combined with the
   card's `celebrating` pulse glow and the toast, this makes
   each purchase feel like a tiny jackpot.
   ========================================================= */
function PurchaseBurst({ color = "#FFE600", onDone }) {
  useEffect(() => {
    const t = setTimeout(() => onDone?.(), 1200);
    return () => clearTimeout(t);
  }, [onDone]);
  const particles = React.useMemo(() => (
    Array.from({ length: 24 }).map((_, i) => {
      const angle = (i / 24) * Math.PI * 2;
      const dist = 90 + Math.random() * 70;
      return {
        id: i,
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist,
        size: 4 + Math.random() * 6,
        delay: Math.random() * 60,
      };
    })
  ), []);
  return (
    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center" aria-hidden="true">
      {/* Flash */}
      <div className="absolute inset-0" style={{
        background: `radial-gradient(circle at center, ${color}55 0%, transparent 60%)`,
        animation: "shop-flash 0.5s ease-out forwards",
      }} />
      {/* Shockwave ring */}
      <div className="absolute w-6 h-6 rounded-full" style={{
        border: `2px solid ${color}`,
        boxShadow: `0 0 20px ${color}`,
        animation: "shop-ring 0.8s ease-out forwards",
      }} />
      {/* Core star */}
      <Sparkles size={56} style={{
        color, filter: `drop-shadow(0 0 18px ${color})`,
        animation: "shop-core 0.9s cubic-bezier(.1,.9,.3,1) forwards",
      }} />
      {/* Burst particles */}
      {particles.map(p => (
        <span key={p.id} className="absolute rounded-full" style={{
          width: p.size, height: p.size,
          background: color,
          boxShadow: `0 0 10px ${color}`,
          animation: `shop-particle 1.0s ${p.delay}ms cubic-bezier(.2,.7,.3,1) forwards`,
          "--dx": `${p.dx}px`, "--dy": `${p.dy}px`,
        }} />
      ))}
      {/* +Purchased banner */}
      <div className="absolute font-heading text-2xl sm:text-3xl font-black tracking-widest" style={{
        color,
        textShadow: `0 0 16px ${color}, 0 0 32px ${color}`,
        animation: "shop-banner 1.0s ease-out forwards",
      }}>PURCHASED!</div>
    </div>
  );
}

function HandshakeShop() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [burstFor, setBurstFor] = useState(null);  // item_id currently showing burst
  const [celebrating, setCelebrating] = useState(null);  // item_id currently pulsing
  const [showDone, setShowDone] = useState(false);
  const spendRain = (id) => handshakeSpendRain(document.querySelector(`[data-testid='handshake-item-${id}']`));
  const triggerCelebration = (itemId) => {
    setBurstFor(itemId);
    setCelebrating(itemId);
    setTimeout(() => setCelebrating(null), 1400);
  };
  const load = async () => {
    const r = await api.get("/handshake-shop/items");
    setItems(r.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const buyPyroUnlock = async () => {
    setBusy("pyro_unlock");
    try {
      const r = await api.post("/handshake-shop/pyro/unlock");
      if (r.data?.already_unlocked) {
        toast.info("Pyro is already unlocked");
      } else {
        spendRain("pyro_unlock");
        toast.success(`🎆 PYRO UNLOCKED! −${r.data.handshakes_spent} 🤝`);
        window.dispatchEvent(new CustomEvent("bulk-cheer", {
          detail: { line: "LIGHT 'EM UP, BROTHER!", durationMs: 3400 },
        }));
      }
      await refresh();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const pyroItem = items.find((i) => i.id === "pyro_unlock");
  const doneCount = items.filter((i) => (i.id === "pyro_unlock" || i.id.startsWith("hof_statue_")) ? i.owned : STACK_META[i.id] && i.maxed).length;

  const renderCards = (done) => (
    <>
        {pyroItem && !!pyroItem.owned === done && (
          <div
            data-testid="handshake-item-pyro_unlock"
            className="shop-card wr-rise group relative rounded-2xl border-2 p-5 overflow-hidden transition-transform duration-300 hover:-translate-y-1"
            style={{
              background: "linear-gradient(135deg, rgba(255,149,0,0.18), rgba(255,0,127,0.12), rgba(10,5,26,0.95))",
              borderColor: pyroItem.owned ? "#39FF14" : "#FF9500",
              boxShadow: pyroItem.owned
                ? "0 0 24px rgba(57,255,20,0.3)"
                : "0 0 28px rgba(255,149,0,0.5)",
            }}
          >
            {pyroItem.owned && (
              <div
                className="absolute top-2 right-2 px-2 py-0.5 rounded-full font-heading text-[10px] uppercase tracking-widest z-10"
                style={{ background: "#39FF14", color: "#0A051A", boxShadow: "0 0 10px #39FF14AA" }}
              >
                ✓ UNLOCKED
              </div>
            )}
            <div className="flex items-center gap-5 flex-wrap">
              <div style={{ color: "#FF9500", filter: "drop-shadow(0 0 12px #FF9500)" }}>
                <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
                </svg>
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="font-graffiti text-3xl leading-tight mb-1" style={{ color: "#FFC266", textShadow: "0 0 12px #FF9500, 2px 2px 0 #000" }}>
                  {pyroItem.name}
                </div>
                <p className="text-sm text-[#E5DCFF] mb-2">{pyroItem.description}</p>
                <div className="flex items-center gap-2 text-xs font-mono">
                  <HandshakeIcon className="w-5 h-5" />
                  <span className="text-white font-bold lb-num text-lg">{pyroItem.cost_handshakes}</span>
                  <span className="text-[#A89BCC]">handshakes</span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {pyroItem.owned ? null : (
                  <button
                    data-testid="handshake-pyro-buy-btn"
                    onClick={buyPyroUnlock}
                    disabled={busy === "pyro_unlock"}
                    className="btn-neon px-5 py-2.5 text-xs font-bold disabled:opacity-50"
                  >
                    {busy === "pyro_unlock" ? <Loader2 className="inline animate-spin" size={14} /> : "UNLOCK"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* HOF statue unlocks (visible only once HOF itself is unlocked) */}
        {items.filter(i => i.id.startsWith("hof_statue_") && !!i.owned === done).map((it) => {
          const cls = it.statue_class;
          const colors = {
            middleweight:  "#00F0FF",
            cruiserweight: "#FFD700",
            tag_team:      "#39FF14",
            wildcard:      "#9D00FF",
          };
          const color = colors[cls] || "#9D00FF";
          const img = `/hof/Statue%20${cls.replace("_"," ").replace(/\b\w/g, (c) => c.toUpperCase())}.png`;
          return (
            <div
              key={it.id}
              data-testid={`handshake-item-${it.id}`}
              className={`shop-card wr-rise group relative rounded-2xl border-2 p-5 overflow-hidden transition-transform duration-300 hover:-translate-y-1 ${celebrating === it.id ? "scale-[1.03]" : ""}`}
              style={{
                background: `linear-gradient(150deg, ${color}30, #110726F2 50%, #0A051A)`, "--shop-glow": color,
                borderColor: it.owned ? "#39FF14" : color,
                boxShadow: celebrating === it.id
                  ? `0 0 44px ${color}, 0 0 80px ${color}66, inset 0 0 30px ${color}66`
                  : it.owned ? "0 0 20px rgba(57,255,20,0.3)" : `0 0 20px ${color}66`,
              }}
            >
              {burstFor === it.id && <PurchaseBurst color={color} onDone={() => setBurstFor(null)} />}
              {it.owned && (
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full font-heading text-[10px] uppercase tracking-widest z-10"
                     style={{ background: "#39FF14", color: "#0A051A", boxShadow: "0 0 10px #39FF14AA" }}>✓ UNLOCKED</div>
              )}
              <div className="pointer-events-none absolute -top-12 -left-12 w-40 h-40 rounded-full blur-3xl opacity-25 group-hover:opacity-50 transition-opacity" style={{ background: color }} />
              <div className="relative flex items-center gap-5 flex-wrap">
                <img src={img} alt={it.name} className="w-16 h-16 object-contain"
                     style={{ filter: `drop-shadow(0 0 10px ${color})` }} />
                <div className="flex-1 min-w-[200px]">
                  <div className="font-graffiti text-2xl leading-tight mb-1" style={{ color, textShadow: `0 0 10px ${color}AA, 2px 2px 0 #000` }}>{it.name}</div>
                  <p className="text-sm text-[#E5DCFF] mb-2">{it.description}</p>
                  <div className="flex items-center gap-2 text-xs font-mono">
                    <HandshakeIcon className="w-5 h-5" />
                    <span className="text-white font-bold lb-num text-lg">{it.cost_handshakes}</span>
                    <span className="text-[#A89BCC]">handshakes</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {it.owned ? (
                    <button onClick={() => nav("/hof")} className="btn-neon px-5 py-2.5 text-xs font-bold">GO TO HOF</button>
                  ) : (
                    <button
                      data-testid={`handshake-${it.id}-buy-btn`}
                      onClick={async () => {
                        setBusy(it.id);
                        try {
                          const r = await api.post(it.endpoint);
                          if (r.data?.already_unlocked) toast.info("Already unlocked");
                          else {
                            spendRain(it.id);
                          toast.success(`🏆 ${it.name.toUpperCase()}! −${r.data.handshakes_spent} 🤝`, { duration: 4000 });
                            triggerCelebration(it.id);
                          }
                          await refresh();
                          await load();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Failed");
                        } finally { setBusy(null); }
                      }}
                      disabled={busy === it.id}
                      className="btn-neon px-5 py-2.5 text-xs font-bold disabled:opacity-50"
                    >
                      {busy === it.id ? <Loader2 className="inline animate-spin" size={14} /> : "UNLOCK"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Rising-cost upgrade stack items (food slots, card drafts, free sparks, pyro energy cap, double induction) */}
        {items.filter(i => STACK_META[i.id] && !!i.maxed === done).map((it) => {
          const meta = STACK_META[it.id];
          const Icon = meta.icon;
          const color = meta.color;
          const maxed = it.maxed;
          const pct = it.max_count ? (it.count / it.max_count) * 100 : 0;
          const nav_target = meta.nav;
          return (
            <div
              key={it.id}
              data-testid={`handshake-item-${it.id}`}
              className={`shop-card wr-rise group relative rounded-2xl border-2 p-5 overflow-hidden transition-transform duration-300 hover:-translate-y-1 ${celebrating === it.id ? "scale-[1.03]" : ""}`}
              style={{
                background: `linear-gradient(150deg, ${color}30, #110726F2 50%, #0A051A)`, "--shop-glow": color,
                borderColor: maxed ? "#39FF14" : color,
                boxShadow: celebrating === it.id
                  ? `0 0 44px ${color}, 0 0 80px ${color}66, inset 0 0 30px ${color}66`
                  : maxed ? "0 0 20px rgba(57,255,20,0.3)" : `0 0 20px ${color}66`,
              }}
            >
              {burstFor === it.id && <PurchaseBurst color={color} onDone={() => setBurstFor(null)} />}
              {maxed && (
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full font-heading text-[10px] uppercase tracking-widest z-10"
                     style={{ background: "#39FF14", color: "#0A051A", boxShadow: "0 0 10px #39FF14AA" }}>✓ MAXED</div>
              )}
              <div className="flex items-center gap-5 flex-wrap">
                <div className="shrink-0 w-16 h-16 flex items-center justify-center transition-transform duration-300 group-hover:rotate-6 group-hover:scale-105">
                  <Icon size={36} style={{ color, filter: `drop-shadow(0 0 8px ${color})` }} />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-graffiti text-2xl leading-tight mb-1" style={{ color, textShadow: `0 0 10px ${color}AA, 2px 2px 0 #000` }}>{it.name}</div>
                  <p className="text-sm text-[#E5DCFF] mb-2">{it.description}</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    {!maxed && (
                      <div className="flex items-center gap-2 text-xs font-mono">
                        <HandshakeIcon className="w-5 h-5" />
                        <span className="text-white font-bold lb-num text-lg">{it.cost_handshakes}</span>
                        <span className="text-[#A89BCC]">handshakes</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-32 rounded-full overflow-hidden bg-[#1a0a2a] border border-[#39194D]">
                        <div className="h-full" style={{ width: `${pct}%`, background: color, boxShadow: `0 0 6px ${color}` }} />
                      </div>
                      <span className="text-[11px] lb-num font-bold text-[#C9BDF0]" data-testid={`handshake-${it.id}-progress`}>
                        {it.count}/{it.max_count}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {maxed ? (
                    nav_target ? (
                      <button data-testid={`handshake-${it.id}-go-btn`} onClick={() => nav(nav_target)} className="btn-neon px-5 py-2.5 text-xs font-bold">GO</button>
                    ) : null
                  ) : (
                    <button
                      data-testid={`handshake-${it.id}-buy-btn`}
                      onClick={async () => {
                        setBusy(it.id);
                        try {
                          const r = await api.post(it.endpoint);
                          spendRain(it.id);
                        toast.success(`✓ ${it.name.toUpperCase()} ${r.data.new_count}/${r.data.max_count} · −${r.data.handshakes_spent} 🤝`, { duration: 4000 });
                          triggerCelebration(it.id);
                          await refresh();
                          await load();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Failed");
                        } finally { setBusy(null); }
                      }}
                      disabled={busy === it.id}
                      className="btn-neon px-5 py-2.5 text-xs font-bold disabled:opacity-50"
                    >
                      {busy === it.id ? <Loader2 className="inline animate-spin" size={14} /> : "BUY"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
    </>
  );

  return (
    <div className="space-y-4" data-testid="handshake-shop">
      <div className="flex items-center gap-3 mb-1">
        <SecretHandshake />
        <div className="min-w-0">
          <h2 className="font-graffiti text-3xl sm:text-4xl leading-none" style={{ color: "#00F0FF", textShadow: "0 0 16px #00F0FFAA, 0 0 32px #9D00FF55, 3px 3px 0 #000" }} data-testid="handshake-shop-title">Handshake Shop</h2>
          <div className="h-[3px] w-40 mt-1.5 rounded-full" style={{ background: "linear-gradient(90deg, #00F0FF, #9D00FF, transparent)" }} />
        </div>
      </div>
      <p className="text-[#A89BCC] font-mono text-xs mb-4">
        Spend hard-earned Handshakes on permanent unlocks and premium perks.
      </p>

      {renderCards(false)}

      {doneCount > 0 && (
        <div className="flex items-center gap-3 pt-4" data-testid="handshake-purchased-divider">
          <div className="h-[1px] flex-1 max-w-[80px]" style={{ background: "linear-gradient(90deg, #39FF1400, #39FF14CC)" }} />
          <h3 className="font-heading text-sm uppercase tracking-[0.3em] text-[#39FF14]" style={{ textShadow: "0 0 8px #39FF1466" }}>Purchased · {doneCount}</h3>
          <button data-testid="handshake-purchased-toggle" onClick={() => setShowDone((v) => !v)} aria-expanded={showDone}
                  aria-label={showDone ? "Hide purchased" : "Show purchased"}
                  className="w-7 h-7 shrink-0 rounded-full border-2 border-[#39FF14]/70 flex items-center justify-center text-[#39FF14] transition-[transform,background-color,box-shadow] hover:bg-[#39FF14]/15 hover:shadow-[0_0_12px_#39FF1488] active:scale-90">
            <ChevronDown size={16} strokeWidth={3} className={`transition-transform duration-300 ${showDone ? "rotate-180" : ""}`} />
          </button>
          <div className="h-[1px] flex-1" style={{ background: "linear-gradient(90deg, #39FF14CC, #39FF1400)" }} />
        </div>
      )}
      {showDone && <div className="space-y-4 tt-unfold" data-testid="handshake-purchased-list">{renderCards(true)}</div>}

      <div className="text-center text-xs font-mono text-[#695D8E] italic pt-4">
        More Handshake items coming soon — gear reroll tokens, cosmetic frames and more.
      </div>
    </div>
  );
}

function PremiumShop() {
  const { refresh } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [pkgs, setPkgs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const [celebrate, setCelebrate] = useState(null);
  const loadPkgs = () => api.get("/payments/packages").then(r => setPkgs(r.data));

  useEffect(() => { loadPkgs(); }, []);

  // Poll status after redirect
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const sid = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      toast.info("Payment cancelled");
      nav("/shop", { replace: true });
      return;
    }
    if (!sid) return;

    let attempts = 0;
    const max = 8;
    setStatusMsg("Verifying payment...");
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid" || r.data.awarded) {
          setStatusMsg("");
          setCelebrate({ handshakes: r.data.handshakes, name: r.data.package_name });
          const c = { left: window.innerWidth / 2 - 40, top: window.innerHeight / 2 - 40, width: 80, height: 80 };
          handshakeRain(c, { count: 14 });
          window.dispatchEvent(new CustomEvent("bulk-cheer"));
          await refresh();
          setTimeout(loadPkgs, 2600);
          nav("/shop", { replace: true });
          return;
        }
        if (r.data.status === "expired" || r.data.payment_status === "failed") {
          setStatusMsg("");
          toast.error("Payment failed or expired.");
          nav("/shop", { replace: true });
          return;
        }
        if (attempts >= max) {
          setStatusMsg("");
          toast.warning("Payment still processing. It will show up automatically.");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setStatusMsg("");
        toast.error("Could not verify payment status.");
      }
    };
    poll();
  }, [loc.search, nav, refresh]);

  const buy = async (pkgId) => {
    setBusy(pkgId);
    try {
      const r = await api.post("/payments/checkout", {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const tiers = pkgs.filter(p => p.id.startsWith("hs_tier_"));
  const bundles = pkgs.filter(p => p.one_time && !p.owned);

  return (
    <div>
      {statusMsg && (
        <div className="mb-4 card-surface p-3 flex items-center gap-2" data-testid="payment-status">
          <Loader2 className="animate-spin neon-cyan" size={16} />
          <span className="font-mono text-sm neon-cyan">{statusMsg}</span>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-3">
        {tiers.map(p => <PremiumCard key={p.id} pkg={p} onBuy={buy} busy={busy} />)}
      </div>
      {bundles.length > 0 && (
        <div className="space-y-6 mt-10" data-testid="bundle-list">
          {bundles.map(b => <BundleBanner key={b.id} pkg={b} onBuy={buy} busy={busy} />)}
        </div>
      )}
      {celebrate && <PurchaseCelebration data={celebrate} onDone={() => setCelebrate(null)} />}
      <div className="mt-6 text-center text-[10px] font-mono text-[#695D8E] tracking-widest">
        SECURED BY STRIPE · TEST MODE · USE CARD 4242 4242 4242 4242 ANY FUTURE EXPIRY · ANY CVC
      </div>
    </div>
  );
}

const BULK_TAB_LINES = {
  hotdog:    "HOT DOGS! I GOT SEVEN KIDS AND THREE EX-WIVES TO FEED!",
  handshake: "LET ME TELL YA SOMETHING! A HANDSHAKE IS AS GOOD AS CASH!",
  premium:   "FINALLY! REAL MONEY! I'M STARVING HERE BROTHER!",
};

export default function ShopPage() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const initialTab = (params.get("session_id") || params.get("cancelled")) ? "premium" : "hotdog";
  const [tab, setTab] = useState(initialTab);

  const switchTab = (id) => {
    if (id === tab) return;
    setTab(id);
    const line = BULK_TAB_LINES[id];
    if (line) {
      window.dispatchEvent(new CustomEvent("bulk-cheer", {
        detail: { line, durationMs: 3600 },
      }));
    }
  };

  const TabBtn = ({ id, label, icon: Icon, testId, color = "#FF007F" }) => {
    const active = tab === id;
    return (
      <button
        data-testid={testId}
        onClick={() => switchTab(id)}
        className={`group flex items-center gap-2 px-5 py-2.5 rounded-full font-heading uppercase tracking-widest text-xs border-2 transition-[transform,background-color,box-shadow,color,border-color] duration-200 hover:-translate-y-0.5
          ${active
            ? "text-white"
            : "bg-[#0A051A]/85 backdrop-blur text-[#A89BCC] border-[#2D1C52] hover:border-[#00F0FF] hover:text-white"}`}
        style={active ? { background: color, borderColor: color, boxShadow: `0 0 20px ${color}AA`, color: color === "#00F0FF" ? "#0A051A" : "#FFFFFF" } : undefined}
      >
        <Icon size={15} className="transition-transform group-hover:rotate-12" /> {label}
      </button>
    );
  };

  return (
    <div className="relative min-h-screen">
      <div className="absolute inset-0 -z-10 opacity-40"
           style={{ backgroundImage: `url(${ASSETS.bg.shop})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#0A051A]/60 via-[#0A051A]/85 to-[#0A051A]" />

      <div className="wr-drift pointer-events-none absolute top-40 left-0 w-80 h-80 rounded-full blur-3xl opacity-20 bg-[#FF007F] -z-10" />
      <div className="wr-drift pointer-events-none absolute top-[60%] right-0 w-80 h-80 rounded-full blur-3xl opacity-15 bg-[#00F0FF] -z-10" style={{ animationDelay: "-6s" }} />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <BulkHoganBanner />
       
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-6">
          <TabBtn id="hotdog" label="Hot Dog Shop" icon={ShoppingCart} testId="shop-tab-hotdog" />
          <TabBtn id="handshake" label="Handshake Shop" icon={Handshake} testId="shop-tab-handshake" color="#00F0FF" />
          <TabBtn id="premium" label="Premium Shop" icon={Crown} testId="shop-tab-premium" />
        </div>

        {tab === "hotdog" ? <HotDogShop /> : tab === "handshake" ? <HandshakeShop /> : <PremiumShop />}
      </div>
    </div>
  );
}
